/**
 * Authentication Utilities
 * Helper functions for authentication and custom claims
 */

import { auth } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Get custom claims from current user
 * @returns {Promise<Object>} Custom claims object
 */
export async function getCustomClaims() {
  const user = auth.currentUser;
  if (!user) return null;
  
  try {
    const idTokenResult = await user.getIdTokenResult();
    return idTokenResult.claims;
  } catch (error) {
    console.error('Error getting custom claims:', error);
    return null;
  }
}

/**
 * Force refresh of ID token to get updated custom claims
 * @returns {Promise<Object>} Updated custom claims
 */
export async function refreshCustomClaims() {
  const user = auth.currentUser;
  if (!user) return null;
  
  try {
    // Force refresh token
    await user.getIdToken(true);
    
    // Get updated claims
    const idTokenResult = await user.getIdTokenResult();
    return idTokenResult.claims;
  } catch (error) {
    console.error('Error refreshing custom claims:', error);
    return null;
  }
}

/**
 * Get user role from custom claims
 * @returns {Promise<string|null>} User role
 */
export async function getUserRole() {
  const claims = await getCustomClaims();
  return claims?.role || null;
}

/**
 * Get user company ID from custom claims
 * @returns {Promise<string|null>} Company ID
 */
export async function getUserCompanyId() {
  const claims = await getCustomClaims();
  return claims?.companyId || null;
}

/**
 * Check if user is super admin
 * @returns {Promise<boolean>} True if super admin
 */
export async function isSuperAdmin() {
  const role = await getUserRole();
  return role === 'super_admin';
}

/**
 * Check if user is company admin
 * @returns {Promise<boolean>} True if company admin
 */
export async function isCompanyAdmin() {
  const role = await getUserRole();
  return role === 'company_admin';
}

/**
 * Check if user is company user
 * @returns {Promise<boolean>} True if company user
 */
export async function isCompanyUser() {
  const role = await getUserRole();
  return role === 'company_user';
}

/**
 * Assign custom claims to a user (super admin only)
 * @param {string} userId - User ID
 * @param {string} role - Role to assign
 * @param {string} companyId - Company ID (optional for super_admin)
 * @returns {Promise<Object>} Result
 */
export async function assignCustomClaims(userId, role, companyId = null) {
  try {
    const functions = getFunctions();
    const assignClaims = httpsCallable(functions, 'assignCustomClaims');
    
    const result = await assignClaims({ userId, role, companyId });
    return result.data;
  } catch (error) {
    console.error('Error assigning custom claims:', error);
    throw error;
  }
}

/**
 * Create a new company with admin user (super admin only)
 * @param {Object} companyData - Company data
 * @returns {Promise<Object>} Result with companyId and userId
 */
export async function createCompany(companyData) {
  try {
    const functions = getFunctions();
    const createCompanyFn = httpsCallable(functions, 'createCompany');
    
    const result = await createCompanyFn(companyData);
    return result.data;
  } catch (error) {
    console.error('Error creating company:', error);
    throw error;
  }
}

/**
 * Create a new user for a company
 * @param {Object} userData - User data
 * @returns {Promise<Object>} Result with userId
 */
export async function createCompanyUser(userData) {
  try {
    const functions = getFunctions();
    const createUserFn = httpsCallable(functions, 'createCompanyUser');
    
    const result = await createUserFn(userData);
    return result.data;
  } catch (error) {
    console.error('Error creating company user:', error);
    throw error;
  }
}

/**
 * Update user role (super admin only)
 * @param {string} userId - User ID
 * @param {string} role - New role
 * @param {string} companyId - Company ID (optional for super_admin)
 * @returns {Promise<Object>} Result
 */
export async function updateUserRole(userId, role, companyId = null) {
  try {
    const functions = getFunctions();
    const updateRoleFn = httpsCallable(functions, 'updateUserRole');
    
    const result = await updateRoleFn({ userId, role, companyId });
    return result.data;
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
}

/**
 * Get user information including custom claims
 * @param {string} userId - User ID (optional, defaults to current user)
 * @returns {Promise<Object>} User information
 */
export async function getUserInfo(userId = null) {
  try {
    const functions = getFunctions();
    const getUserInfoFn = httpsCallable(functions, 'getUserInfo');
    
    const result = await getUserInfoFn({ userId });
    return result.data;
  } catch (error) {
    console.error('Error getting user info:', error);
    throw error;
  }
}

/**
 * Wait for custom claims to be available after user creation
 * Polls for claims with exponential backoff
 * @param {number} maxAttempts - Maximum number of attempts (default: 10)
 * @param {number} initialDelay - Initial delay in ms (default: 500)
 * @returns {Promise<Object>} Custom claims
 */
export async function waitForCustomClaims(maxAttempts = 10, initialDelay = 500) {
  let attempts = 0;
  let delay = initialDelay;
  
  while (attempts < maxAttempts) {
    const claims = await refreshCustomClaims();
    
    if (claims && claims.role) {
      return claims;
    }
    
    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, delay));
    
    // Exponential backoff
    delay = Math.min(delay * 2, 5000);
    attempts++;
  }
  
  throw new Error('Custom claims not available after maximum attempts');
}

/**
 * Get redirect path based on user role
 * @param {string} role - User role
 * @returns {string} Redirect path
 */
export function getRedirectPathForRole(role) {
  switch (role) {
    case 'super_admin':
      return '/super-admin';
    case 'company_admin':
      return '/admin';
    case 'company_user':
      return '/';
    default:
      return '/';
  }
}

/**
 * Check if user has permission for an action
 * @param {string} userRole - User role
 * @param {string} requiredRole - Required role
 * @returns {boolean} True if user has permission
 */
export function hasPermission(userRole, requiredRole) {
  const roleHierarchy = {
    'super_admin': 3,
    'company_admin': 2,
    'company_user': 1
  };
  
  const userLevel = roleHierarchy[userRole] || 0;
  const requiredLevel = roleHierarchy[requiredRole] || 0;
  
  return userLevel >= requiredLevel;
}
